/**
 * 🎬 /demo — Page démo CoachDIA cinematic plein écran
 * ===================================================
 *
 * RÈGLES PRODUIT (validées) :
 *   - Accessible UNIQUEMENT via le bouton "Voir la démo" de la home
 *     (guard via `location.state.fromDemoTrigger` — l'accès direct
 *      par URL externe redirige vers `/`).
 *   - 100vw × 100dvh, fond cinematic foncé (zéro modal centré).
 *   - 12 scènes séquentielles (data/demoScenes.config.js) avec
 *     voiceover OpenAI TTS + sous-titres synchronisés.
 *   - Navigation : auto-progression, flèches clavier, swipe mobile,
 *     barre de progression chapitre.
 *   - Audio démarre `muted` (autoplay-safe) ; CTA "Activer le son"
 *     premium et persistant.
 *   - Zéro fallback : si une scène / un audio échoue → message
 *     explicite + bouton "Passer à la suivante".
 *   - CTA finale → `/subscription`.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Volume2,
  VolumeX,
  AlertTriangle,
  Pause,
  Play,
} from 'lucide-react';

import { DEMO_SCENES, DEMO_SCENE_COUNT, DEMO_TOTAL_DURATION_MIN } from '../data/demoScenes.config';
import { T } from '../components/HeroDemoVideo';
import useScreenWakeLock from '../hooks/useScreenWakeLock';

const API = process.env.REACT_APP_BACKEND_URL;

/* ============================================================
   🎚️ Stage — affiche la scène (Frame) en grand format cinematic
   ============================================================ */
const Stage = ({ scene, sceneError, onSkip, onPrimaryCta, onSecondaryCta }) => {
  const Frame = scene.Frame;
  return (
    <div
      className="relative w-full h-full flex items-center justify-center overflow-hidden px-2 sm:px-4"
      data-testid="demo-stage"
    >
      {/* Halo doré dérivant lent (cinematic) */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{
          backgroundPosition: ['0% 35%', '100% 65%', '0% 35%'],
        }}
        transition={{ duration: 22, ease: 'easeInOut', repeat: Infinity }}
        style={{
          backgroundImage:
            'radial-gradient(50% 45% at 50% 50%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 70%)',
          backgroundSize: '180% 180%',
        }}
      />
      {/* Vignettage cinematic (toutes scènes) */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 50%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.40) 100%)',
        }}
      />
      {/* Grain subtil (texture cinéma documentaire) */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.045] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.7 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          backgroundSize: '160px 160px',
        }}
      />
      <AnimatePresence mode="sync">
        {sceneError ? (
          <motion.div
            key="err"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-10 flex items-center justify-center max-w-md mx-auto px-6 text-center"
            data-testid="demo-scene-error"
          >
            <div
              className="rounded-2xl p-6"
              style={{
                background: 'rgba(255,255,255,0.95)',
                border: `1px solid ${T.border}`,
                boxShadow: '0 20px 50px -20px rgba(0,0,0,0.5)',
              }}
            >
              <AlertTriangle
                className="w-8 h-8 mx-auto mb-2"
                style={{ color: '#C44B6F' }}
              />
              <div
                className="font-serif text-lg font-semibold mb-2"
                style={{ color: T.ink }}
              >
                Cette scène est temporairement indisponible.
              </div>
              <div className="text-sm mb-4" style={{ color: T.inkSoft }}>
                {sceneError}
              </div>
              <button
                type="button"
                onClick={onSkip}
                data-testid="demo-skip-error-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full text-sm font-semibold"
                style={{
                  background: T.ink,
                  color: T.goldLight,
                }}
              >
                Passer à la suivante
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={scene.id}
            initial={{ opacity: 0, scale: 1.015, filter: 'blur(6px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.99, filter: 'blur(4px)' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 z-10 flex items-center justify-center"
          >
            <div className="relative w-full h-full max-w-[820px] sm:max-w-[820px] sm:max-h-[min(76dvh,580px)] sm:aspect-[16/10] sm:h-auto">
            <div
              className="absolute inset-0 rounded-[24px] overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAF8 100%)',
                border: `1px solid rgba(212,175,55,0.30)`,
                boxShadow:
                  '0 40px 80px -30px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.18)',
              }}
            >
              {scene.id === 'transformation' ? (
                <Frame
                  onPrimaryCta={onPrimaryCta}
                  onSecondaryCta={onSecondaryCta}
                />
              ) : (
                <Frame />
              )}
            </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ============================================================
   🧭 Chapter rail — barre de chapitres haut de page
   ============================================================ */
const ChapterRail = ({ activeIndex }) => (
  <div
    className="flex items-center gap-1 px-3 sm:px-8 pt-3 sm:pt-5"
    aria-hidden
  >
    {DEMO_SCENES.map((s, i) => (
      <span
        key={s.id}
        className="flex-1 rounded-full transition-all duration-500"
        style={{
          height: 3,
          background:
            i < activeIndex
              ? T.gold
              : i === activeIndex
              ? `linear-gradient(90deg, ${T.gold}, ${T.goldLight})`
              : 'rgba(255,255,255,0.18)',
          boxShadow: i === activeIndex ? `0 0 8px ${T.gold}aa` : 'none',
        }}
      />
    ))}
  </div>
);

/* ============================================================
   📜 Captions — sous-titres premium
   ============================================================ */
const Captions = ({ scene }) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={`${scene.id}-cap`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="px-3 sm:px-8 pb-1 sm:pb-2"
      data-testid="demo-captions"
    >
      <div
        className="mx-auto max-w-3xl rounded-2xl px-3 py-2 sm:px-5 sm:py-4 backdrop-blur-md flex items-center gap-2 sm:gap-3"
        style={{
          background: 'rgba(28,17,71,0.55)',
          border: '1px solid rgba(212,175,55,0.32)',
          boxShadow: '0 18px 40px -16px rgba(0,0,0,0.5)',
        }}
      >
        <span
          className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold flex-shrink-0"
          style={{ color: T.goldLight }}
        >
          {scene.kicker}
        </span>
        <span
          className="text-[13px] sm:text-[15px] font-medium text-white leading-snug"
        >
          {scene.subtitle}
        </span>
      </div>
    </motion.div>
  </AnimatePresence>
);

/* ============================================================
   🎮 ControlBar — contrôles bas de page
   ============================================================ */
const ControlBar = ({
  index,
  onPrev,
  onNext,
  onClose,
  isPlaying,
  togglePlay,
  isMuted,
  toggleMute,
}) => (
  <div
    className="px-3 sm:px-8 pb-3 sm:pb-8 pt-1 sm:pt-2 flex items-center justify-between gap-2 sm:gap-3"
    style={{
      paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0.75rem)',
    }}
  >
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onPrev}
        aria-label="Scène précédente"
        data-testid="demo-prev-btn"
        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all hover:bg-white/10"
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          color: T.goldLight,
        }}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Lecture'}
        data-testid="demo-playpause-btn"
        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all hover:bg-white/10"
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          color: T.goldLight,
        }}
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <button
        type="button"
        onClick={onNext}
        aria-label="Scène suivante"
        data-testid="demo-next-btn"
        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all hover:bg-white/10"
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          color: T.goldLight,
        }}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>

    <span
      className="text-[11px] sm:text-[12px] font-semibold tabular-nums tracking-[0.16em] uppercase"
      style={{ color: 'rgba(243,208,96,0.78)' }}
      data-testid="demo-scene-counter"
    >
      {String(index + 1).padStart(2, '0')} / {String(DEMO_SCENE_COUNT).padStart(2, '0')}
    </span>

    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggleMute}
        aria-label={isMuted ? 'Activer le son' : 'Couper le son'}
        data-testid="demo-mute-btn"
        className="inline-flex items-center gap-2 px-3 sm:px-4 h-10 sm:h-11 rounded-full text-[12px] font-semibold transition-all"
        style={{
          background: isMuted
            ? `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`
            : 'rgba(255,255,255,0.08)',
          color: isMuted ? T.ink : T.goldLight,
          border: isMuted ? 'none' : '1px solid rgba(255,255,255,0.18)',
          boxShadow: isMuted ? `0 8px 22px -8px ${T.gold}aa` : 'none',
        }}
      >
        {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        <span className="hidden sm:inline">
          {isMuted ? 'Activer le son' : 'Son activé'}
        </span>
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer la démo"
        data-testid="demo-close-btn"
        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all hover:bg-white/10"
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          color: T.goldLight,
        }}
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  </div>
);

/* ============================================================
   🎬 Page principale
   ============================================================ */
export default function Demo() {
  const navigate = useNavigate();
  const location = useLocation();

  // 🔒 Guard : la page ne s'ouvre que via le bouton "Voir la démo" OU
  // via un lien de partage public (utm_source=demo_share). Cela préserve la
  // protection contre les crawlers/bots tout en autorisant les visiteurs qui
  // reçoivent un lien partagé sur LinkedIn, X, ou par message.
  const fromDemoTrigger =
    location.state && location.state.fromDemoTrigger === true;
  const fromShareLink = (() => {
    try {
      const p = new URLSearchParams(location.search);
      return p.get('utm_source') === 'demo_share' || p.get('export') === '1';
    } catch {
      return false;
    }
  })();
  const allowed = fromDemoTrigger || fromShareLink;

  // 🎬 Mode EXPORT VIDÉO — déclenché par ?export=1 côté backend (Playwright).
  // Bypass complet du guard + overlay start + contrôles masqués.
  // Force narration + musique sans user-gesture (Chromium lancé avec
  // --autoplay-policy=no-user-gesture-required côté backend).
  const exportMode = (() => {
    try {
      return new URLSearchParams(location.search).get('export') === '1';
    } catch {
      return false;
    }
  })();

  const [index, setIndex] = useState(0);
  // 🎬 Démarrage : si l'utilisateur arrive via un lien partagé (sans user-gesture
  // depuis notre app), on démarre en pause + overlay premium "Lancer avec le son".
  // Sinon (clic "Voir la démo" ou mode export), démarrage immédiat avec son.
  const [isPlaying, setIsPlaying] = useState(!fromShareLink || exportMode);
  const [isMuted, setIsMuted] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  // 🎯 Overlay "Lancer avec le son" — premier tap = user gesture qui débloque audio.
  const [showStartOverlay, setShowStartOverlay] = useState(fromShareLink && !exportMode);
  const [sceneError, setSceneError] = useState(null);
  // 🎯 Overlay final + auto-redirect /subscription (apparaît à la fin scène 13)
  const [showFinalOverlay, setShowFinalOverlay] = useState(false);
  const finalCountdownRef = useRef(null);
  const audioRef = useRef(null);
  const ambientRef = useRef(null);
  const ambientFadeIntervalRef = useRef(null);
  const swipeStartX = useRef(null);
  // 🎚️ Mutex unique pour TOUTES les modulations volume musique
  // (transition scène + ducking voix). Évite les chevauchements de rampes.
  const volumeRampRef = useRef(null);

  // 🎚️ Web Audio API — pilote universel du volume musique.
  //
  // 🔴 Pourquoi : iOS Safari/WebKit IGNORE silencieusement
  //    HTMLMediaElement.volume (Apple Dev Docs : « The volume property is
  //    not settable in JavaScript. Reading always returns 1. »). Conséquence
  //    pré-fix : la musique jouait à 100 % sur iPhone et couvrait la voix.
  //
  // ✅ Fix structurel : on intercepte le flux du <audio> ambient via
  //    MediaElementAudioSourceNode → GainNode → destination. GainNode est
  //    respecté sur TOUS les navigateurs (Web Audio API). Aucun profil
  //    iOS spécifique : Android/desktop reçoivent le même chemin et le
  //    même résultat audible.
  //
  // ⚙️ `ambientGainStateRef` est la SOURCE DE VÉRITÉ du volume musique
  //    (utilisée à la place de `ambient.volume` pour les lectures, car
  //    iOS renvoie toujours 1.0 sur cette propriété). Toutes les rampes
  //    lisent et écrivent via `getAmbientGain()` / `setAmbientGain()`.
  const audioCtxRef = useRef(null);
  const gainNodeRef = useRef(null);
  const audioSourceNodeRef = useRef(null);
  const audioGraphReadyRef = useRef(false);
  const ambientGainStateRef = useRef(0);

  const getAmbientGain = useCallback(() => ambientGainStateRef.current, []);

  const setAmbientGain = useCallback((value) => {
    const v = Math.max(0, Math.min(1, value));
    ambientGainStateRef.current = v;
    if (audioGraphReadyRef.current && gainNodeRef.current) {
      // Source de vérité : GainNode (respecté sur iOS + tous navigateurs).
      gainNodeRef.current.gain.value = v;
    } else if (ambientRef.current) {
      // Pré-init (avant user-gesture) : on alimente .volume en attendant.
      // Sur iOS ce write sera ignoré, mais ambientGainStateRef reste fiable
      // et sera projeté sur le gainNode dès qu'il sera initialisé.
      ambientRef.current.volume = v;
    }
  }, []);

  // 🔧 Initialise (une seule fois) le graphe Web Audio. Doit être appelé
  //    APRÈS un user-gesture réel (sinon AudioContext suspendu silencieusement
  //    sur iOS Safari / Android Chrome → audio détourné dans le vide).
  //    Idempotent : sûr d'être appelé plusieurs fois ; chaque appel tente un
  //    `ctx.resume()` pour réveiller le contexte s'il s'est endormi.
  const ensureAudioGraph = useCallback(() => {
    const ambient = ambientRef.current;
    if (!ambient) return false;

    // ⚙️ Si le graphe existe déjà, on s'assure simplement que le contexte
    //    n'est pas suspended et on retourne (PAS d'early-return aveugle).
    if (audioGraphReadyRef.current) {
      const existingCtx = audioCtxRef.current;
      if (existingCtx && existingCtx.state === 'suspended' && typeof existingCtx.resume === 'function') {
        existingCtx.resume().catch(() => { /* best-effort */ });
      }
      return true;
    }

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return false;
      const ctx = audioCtxRef.current || new AudioCtx();
      audioCtxRef.current = ctx;
      // createMediaElementSource ne peut être appelé qu'UNE seule fois par
      // élément <audio>. On garde la node créée dans une ref pour idempotence.
      if (!audioSourceNodeRef.current) {
        audioSourceNodeRef.current = ctx.createMediaElementSource(ambient);
      }
      const gain = ctx.createGain();
      gain.gain.value = ambientGainStateRef.current;
      audioSourceNodeRef.current.connect(gain);
      gain.connect(ctx.destination);
      gainNodeRef.current = gain;
      audioGraphReadyRef.current = true;
      // Safari/Android peuvent créer le contexte en état "suspended" même
      // après gesture → resume() systématique.
      if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
        ctx.resume().catch(() => { /* best-effort */ });
      }
      return true;
    } catch (err) {
      // Ne JAMAIS faire échouer la démo : on retombe sur .volume legacy.
      // (Android/desktop fonctionnent toujours via .volume, iOS aura juste
      // la musique trop forte comme avant — pas pire qu'aujourd'hui.)
      // eslint-disable-next-line no-console
      console.warn('[Demo] Web Audio graph init failed:', err?.name || err);
      return false;
    }
  }, []);


  // 🔆 Wake Lock — empêche la mise en veille pendant que la démo joue.
  // Conditions cumulées : isPlaying actif, pas d'overlay de démarrage, pas
  // d'overlay final, pas en mode export (pas pertinent pour Playwright headless).
  // NB : le mute n'éteint PAS le Wake Lock — l'utilisateur regarde la vidéo
  // même sans son, l'écran doit rester actif.
  const wakeLockActive = isPlaying && !showStartOverlay && !showFinalOverlay && !exportMode;
  useScreenWakeLock(wakeLockActive);

  const scene = DEMO_SCENES[index];
  const nextScene = DEMO_SCENES[index + 1];

  const goNext = useCallback(() => {
    setSceneError(null);
    setIndex((i) => (i < DEMO_SCENE_COUNT - 1 ? i + 1 : i));
  }, []);

  const goPrev = useCallback(() => {
    setSceneError(null);
    setIndex((i) => (i > 0 ? i - 1 : 0));
  }, []);

  const closeDemo = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    navigate('/');
  }, [navigate]);

  const handlePrimaryCta = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    navigate('/subscription');
  }, [navigate]);

  const handleSecondaryCta = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    navigate('/subscription');
  }, [navigate]);

  const togglePlay = useCallback(() => {
    setIsPlaying((p) => !p);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((m) => !m);
    setAutoplayBlocked(false);
  }, []);

  // 🎙️ Audio source pour la scène courante
  const audioSrc = useMemo(
    () => `${API}/api/demo/voiceover/${scene.id}.mp3`,
    [scene.id],
  );

  // Audio control : load + play à chaque scène, gestion mute, gestion pause.
  // Si le navigateur refuse `play()` à cause de l'autoplay (rare car la démo
  // s'ouvre sur un user-gesture), on bascule en muted + on signale via
  // `autoplayBlocked` pour mettre en avant le bouton « Activer le son ».
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    a.muted = isMuted;
    a.currentTime = 0;
    if (isPlaying && !sceneError) {
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          if (!err || err.name === 'AbortError') return; // changement rapide de scène — bénin
          if (err.name === 'NotAllowedError') {
            // Autoplay avec son refusé : on retombe muted pour permettre le visuel
            setAutoplayBlocked(true);
            setIsMuted(true);
            a.muted = true;
            a.play().catch(() => { /* ignore — geste utilisateur requis */ });
          } else {
            // eslint-disable-next-line no-console
            console.warn('[Demo] audio.play() error:', err?.name || err);
          }
        });
      }
    } else {
      a.pause();
    }
    return undefined;
  }, [scene.id, isPlaying, isMuted, sceneError]);

  // Sync mute state quand on toggle pendant la lecture
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // ⏱️ Auto-progression : préférer la durée réelle de l'audio (synchro
  // parfaite voiceover ↔ scène) ; à défaut, fallback sur durationMs du script.
  useEffect(() => {
    if (!isPlaying || sceneError) return undefined;
    const a = audioRef.current;
    let timer = null;

    const scheduleFromAudio = () => {
      const dur = a && a.duration && Number.isFinite(a.duration) ? a.duration : null;
      const ms = dur ? dur * 1000 + 400 : scene.durationMs;
      timer = setTimeout(() => {
        if (index < DEMO_SCENE_COUNT - 1) goNext();
      }, ms);
    };

    if (a && a.readyState >= 1 && a.duration && Number.isFinite(a.duration)) {
      scheduleFromAudio();
    } else if (a) {
      const onMeta = () => {
        if (timer) clearTimeout(timer);
        scheduleFromAudio();
      };
      a.addEventListener('loadedmetadata', onMeta, { once: true });
      // Fallback si l'audio ne charge pas dans le temps imparti
      timer = setTimeout(() => {
        if (index < DEMO_SCENE_COUNT - 1) goNext();
      }, scene.durationMs);
      return () => {
        a.removeEventListener('loadedmetadata', onMeta);
        if (timer) clearTimeout(timer);
      };
    } else {
      timer = setTimeout(() => {
        if (index < DEMO_SCENE_COUNT - 1) goNext();
      }, scene.durationMs);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [scene.id, scene.durationMs, isPlaying, index, sceneError, goNext]);

  // Préchargement de la scène suivante (audio uniquement)
  useEffect(() => {
    if (!nextScene) return undefined;
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'audio';
    link.href = `${API}/api/demo/voiceover/${nextScene.id}.mp3`;
    document.head.appendChild(link);
    return () => {
      try { document.head.removeChild(link); } catch (_) { /* noop */ }
    };
  }, [nextScene]);

  // 🎹 Clavier : Esc ferme · ←/→ navigation · Espace play/pause · M mute
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeDemo();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'm' || e.key === 'M') toggleMute();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeDemo, goNext, goPrev, togglePlay, toggleMute]);

  // 👆 Swipe mobile (touch)
  const onTouchStart = (e) => {
    swipeStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (swipeStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - swipeStartX.current;
    if (Math.abs(dx) > 60) {
      if (dx > 0) goPrev();
      else goNext();
    }
    swipeStartX.current = null;
  };

  // 💤 Pause si onglet inactif (Performance & UX)
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // ❌ Erreur audio (ne fait JAMAIS échouer la scène silencieusement —
  //    on log et on garde la scène visuelle visible)
  const onAudioError = useCallback(() => {
    // eslint-disable-next-line no-console
    console.warn(`[Demo] Audio scène '${scene.id}' indisponible — scène visuelle conservée.`);
  }, [scene.id]);

  // 🎵 Musique de fond cinematic — fade-in au mount, fade-out au unmount,
  // sync avec mute global, jamais en concurrence avec la voix.
  useEffect(() => {
    const ambient = ambientRef.current;
    if (!ambient) return undefined;

    const TARGET_VOLUME = 0.07; // Base d'ouverture audible (cordes longues émotionnelles).
    const FADE_IN_MS = 2000;
    const FADE_STEPS = 40;

    const clearFade = () => {
      if (ambientFadeIntervalRef.current) {
        clearInterval(ambientFadeIntervalRef.current);
        ambientFadeIntervalRef.current = null;
      }
    };

    const fadeTo = (target, durationMs) => {
      clearFade();
      const start = getAmbientGain();
      const delta = target - start;
      let step = 0;
      ambientFadeIntervalRef.current = setInterval(() => {
        step += 1;
        const ratio = Math.min(1, step / FADE_STEPS);
        setAmbientGain(start + delta * ratio);
        if (ratio >= 1) clearFade();
      }, durationMs / FADE_STEPS);
    };

    setAmbientGain(0);
    ambient.muted = isMuted;
    ambient.loop = true;
    // ⚙️ NE PAS initialiser le graphe Web Audio ici (pas de user-gesture
    //    garanti au mount → AudioContext serait créé "suspended" et
    //    `createMediaElementSource` détournerait l'audio vers le vide).
    //    L'init est différée :
    //      • soit dans la promesse de play() ci-dessous (preuve que le
    //        navigateur autorise la lecture sonore = gesture valide) ;
    //      • soit dans le onClick de l'audio-gate (gesture explicite).
    //    Tant que le graphe n'est pas init, `setAmbientGain` retombe sur
    //    `ambient.volume` (pipeline natif fonctionnel sur Android/desktop).
    const startPromise = ambient.play();
    if (startPromise && typeof startPromise.catch === 'function') {
      startPromise
        .then(() => {
          // ✅ play() résolu = navigateur OK pour audio sonore = gesture valide.
          //    Sûr d'initialiser Web Audio sans détourner vers un ctx suspendu.
          ensureAudioGraph();
        })
        .catch(() => { /* autoplay refusé : musique restera en pause silencieuse */ });
    }
    fadeTo(TARGET_VOLUME, FADE_IN_MS);

    return () => {
      clearFade();
      // Fade-out rapide à la fermeture
      const fadeOutSteps = 15;
      const fadeOutMs = 600;
      const start = getAmbientGain();
      let step = 0;
      const interval = setInterval(() => {
        step += 1;
        setAmbientGain(start * (1 - step / fadeOutSteps));
        if (step >= fadeOutSteps) {
          clearInterval(interval);
          ambient.pause();
        }
      }, fadeOutMs / fadeOutSteps);
    };
  }, []);

  // Sync mute global (voix + musique)
  useEffect(() => {
    if (ambientRef.current) ambientRef.current.muted = isMuted;
  }, [isMuted]);

  // 🎛️ Sync play/pause global — le bouton Pause doit arrêter la musique ET
  // la narration ensemble. Le bouton Lecture reprend les deux sans restart
  // (pas de mutation de `src`, pas de currentTime = 0, position préservée).
  useEffect(() => {
    const ambient = ambientRef.current;
    if (!ambient) return;
    if (isPlaying) {
      // Reprise : on relance simplement. Si autoplay refuse (rare après geste),
      // on ignore silencieusement — l'utilisateur cliquera à nouveau.
      const p = ambient.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      ambient.pause();
    }
  }, [isPlaying]);

  // Pause musique si onglet inactif (déjà géré pour la voix par visibilitychange)
  useEffect(() => {
    const onVis = () => {
      if (document.hidden && ambientRef.current) ambientRef.current.pause();
      else if (!document.hidden && ambientRef.current && isPlaying) {
        ambientRef.current.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isPlaying]);

  // 🎵 AUDIO CINEMA CONTINU — la musique ne se met JAMAIS en pause sur
  // changement de scène ni sur sceneError. Seul l'onglet caché la pause
  // (visibilitychange). Garantit une continuité sonore cinematic-grade.
  // (L'ancien useEffect [isPlaying, sceneError] qui appelait `a.pause()` est
  //  supprimé : il créait une coupure perceptible entre 2 scènes lorsque
  //  sceneError flickait pendant le swap.)

  // 🎚️🎙️ STATE MACHINE AUDIO UNIFIÉ — toutes les modulations volume musique
  // passent par `startRamp()` avec un mutex (`volumeRampRef`). Élimine
  // totalement les chevauchements de rampes (transition scène vs ducking voix)
  // qui causaient des micro-coupures perceptibles.
  //
  // Volume cible scène = `scene.audioPeak` (0.07 base → 0.11 pic certificat).
  // Drop transition courte → ramp vers le pic. Ducking voix : peak × 0.72.
  useEffect(() => {
    const a = ambientRef.current;
    const v = audioRef.current;
    if (!a || !v) return undefined;

    const targetPeak = typeof scene.audioPeak === 'number' ? scene.audioPeak : 0.09;
    const TRANSITION_DUCK = 0.05;
    const DUCK_RATIO = 0.72;
    const isLastScene = index >= DEMO_SCENE_COUNT - 1;
    const FADE_OUT_MS = 2200;
    const STEPS = 16;

    // Mutex de rampe : annule TOUJOURS la précédente avant d'en démarrer une nouvelle.
    const cancelRamp = () => {
      if (volumeRampRef.current) {
        if (volumeRampRef.current.interval) clearInterval(volumeRampRef.current.interval);
        if (volumeRampRef.current.afterTimer) clearTimeout(volumeRampRef.current.afterTimer);
        volumeRampRef.current = null;
      }
    };
    const startRamp = (from, to, durationMs, after = null) => {
      cancelRamp();
      const clamped = (x) => Math.max(0, Math.min(1, x));
      if (durationMs <= 0 || Math.abs(to - from) < 0.001) {
        setAmbientGain(clamped(to));
        if (after) after();
        return;
      }
      const stepDelta = (to - from) / STEPS;
      let i = 0;
      const interval = setInterval(() => {
        i += 1;
        setAmbientGain(clamped(from + stepDelta * i));
        if (i >= STEPS) {
          clearInterval(interval);
          if (volumeRampRef.current && volumeRampRef.current.interval === interval) {
            volumeRampRef.current = null;
          }
          if (after) after();
        }
      }, durationMs / STEPS);
      volumeRampRef.current = { interval, afterTimer: null };
    };

    // 🎬 Transition scène : drop court → wait → ramp vers peak (ou ducked si voix joue)
    const startVol = getAmbientGain() || targetPeak;
    startRamp(startVol, TRANSITION_DUCK, 150, () => {
      const t = setTimeout(() => {
        const voiceIsPlaying = !v.paused && !v.ended && v.currentTime > 0;
        const ref = targetPeakRef.current;
        const finalTarget = voiceIsPlaying ? ref * DUCK_RATIO : ref;
        startRamp(TRANSITION_DUCK, finalTarget, 280);
      }, 90);
      if (volumeRampRef.current) volumeRampRef.current.afterTimer = t;
    });

    // 🎬 Rampe émotionnelle (climax scène 13) — glissement progressif
    //    audioPeak → audioPeakEnd sur la durée de la scène, sans rupture.
    //    Coexiste proprement avec le ducking voix : on ne touche le volume
    //    QUE si la voix n'est pas active. Sinon on met juste à jour la cible
    //    de référence pour que le prochain `onVoicePause` reprenne le bon niveau.
    const targetPeakRef = { current: targetPeak };
    const audioPeakEnd = scene.audioPeakEnd;
    let emoInterval = null;
    if (
      typeof audioPeakEnd === 'number' &&
      Math.abs(audioPeakEnd - targetPeak) > 0.001
    ) {
      const startTime = Date.now();
      const totalMs = Math.max(0, (scene.durationMs || 12000) - 800);
      emoInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const t = Math.min(1, elapsed / totalMs);
        const newPeak = targetPeak + (audioPeakEnd - targetPeak) * t;
        targetPeakRef.current = newPeak;
        const voiceIsPlaying = !v.paused && !v.ended && v.currentTime > 0;
        if (!voiceIsPlaying) {
          // Pas de chevauchement avec une rampe en cours
          if (!volumeRampRef.current) {
            setAmbientGain(newPeak);
          }
        }
        if (t >= 1) {
          clearInterval(emoInterval);
          emoInterval = null;
        }
      }, 200);
    }

    // 🎙️ Ducking voix-active — TOUT passe par le même `startRamp` → zéro chevauchement.
    //    Utilise `targetPeakRef.current` pour suivre la rampe émotionnelle scène 13.
    const onVoicePlay = () =>
      startRamp(getAmbientGain(), targetPeakRef.current * DUCK_RATIO, 350);
    const onVoicePause = () =>
      startRamp(getAmbientGain(), targetPeakRef.current, 450);
    const onVoiceEnded = () => {
      if (isLastScene) {
        // 🎬 V3 Premium (12/02/2026) — fin scène 13 :
        //   ❌ SUPPRIMÉ : auto-redirect /subscription, overlay final, fade musique à zéro.
        //   ✅ Owen s'arrête. La scène 13 reste affichée intacte avec ses CTAs existants.
        //   ✅ La musique reste à 50% du volume cible et continue à boucler (attribut loop
        //      sur l'élément audio ambient) — l'utilisateur garde le contrôle.
        startRamp(getAmbientGain(), targetPeakRef.current * 0.5, FADE_OUT_MS);
      } else {
        startRamp(getAmbientGain(), targetPeakRef.current, 450);
      }
    };

    v.addEventListener('play', onVoicePlay);
    v.addEventListener('pause', onVoicePause);
    v.addEventListener('ended', onVoiceEnded);

    return () => {
      v.removeEventListener('play', onVoicePlay);
      v.removeEventListener('pause', onVoicePause);
      v.removeEventListener('ended', onVoiceEnded);
      if (emoInterval) {
        clearInterval(emoInterval);
        emoInterval = null;
      }
      cancelRamp();
    };
  }, [scene.id, scene.audioPeak, scene.audioPeakEnd, scene.durationMs, index]);

  // 🎬 V3 Premium (12/02/2026) — l'auto-redirect /subscription après showFinalOverlay
  //    a été RETIRÉ. L'utilisateur décide seul s'il clique "Commencer maintenant",
  //    "Découvrir les parcours", ou revient en arrière via les contrôles existants.

  // Cleanup unmount : annuler tous timers + arrêter musique
  useEffect(() => {
    return () => {
      if (finalCountdownRef.current) clearTimeout(finalCountdownRef.current);
    };
  }, []);

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  // 🎯 Auto-redirect / cleanup
  // (placé hors return pour éviter remount/relance entre scènes)
  // Auto-redirect 4 s vers /subscription après ouverture overlay final.
  // L'utilisateur peut interagir (S'abonner / Découvrir) avant.

  return (
    <div
      className="fixed inset-0 z-[10000] flex flex-col"
      style={{
        width: '100vw',
        height: '100dvh',
        background:
          'radial-gradient(120% 90% at 50% 0%, #1A0F44 0%, #0A0820 60%, #050314 100%)',
        color: T.goldLight,
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="demo-page"
    >
      {/* 🎙️ Audio (un seul élément réutilisé entre scènes) */}
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="auto"
        onError={onAudioError}
        onEnded={() => {
          if (index < DEMO_SCENE_COUNT - 1) goNext();
        }}
      />

      {/* 🎵 Musique de fond cinematic — Gladiator (audio singleton, loop, ducking) */}
      <audio
        ref={ambientRef}
        src="/audio/demo-ambient.mp3"
        preload="auto"
        loop
        data-testid="demo-ambient-audio"
      />

      <ChapterRail activeIndex={index} />

      {/* Header brand */}
      <div className="px-4 sm:px-8 pt-2 sm:pt-3 pb-1 flex items-center gap-2">
        <span
          className="text-[9px] sm:text-[11px] uppercase tracking-[0.28em] font-semibold"
          style={{ color: T.goldLight }}
        >
          CoachDIA · Démo
        </span>
      </div>

      {/* Stage */}
      <div className="flex-1 min-h-0">
        <Stage
          scene={scene}
          sceneError={sceneError}
          onSkip={goNext}
          onPrimaryCta={handlePrimaryCta}
          onSecondaryCta={handleSecondaryCta}
        />
      </div>

      {/* Captions */}
      <Captions scene={scene} />

      {/* Message explicite si l'autoplay avec son a été bloqué par le navigateur */}
      {autoplayBlocked && isMuted && !exportMode && (
        <div
          className="px-4 sm:px-8 -mt-1 mb-1 flex justify-center"
          data-testid="demo-autoplay-blocked-banner"
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold"
            style={{
              background: 'rgba(243,208,96,0.14)',
              color: T.goldLight,
              border: '1px solid rgba(243,208,96,0.42)',
            }}
          >
            <Volume2 className="w-3.5 h-3.5" />
            Votre navigateur a bloqué le son. Cliquez sur « Activer le son ».
          </div>
        </div>
      )}

      {/* Controls — masqués en mode export vidéo */}
      {!exportMode && (
        <ControlBar
          index={index}
          onPrev={goPrev}
          onNext={goNext}
          onClose={closeDemo}
          isPlaying={isPlaying}
          togglePlay={togglePlay}
          isMuted={isMuted}
          toggleMute={toggleMute}
        />
      )}

      {/* 🎬 Overlay premium « Lancer avec le son » — visiteurs arrivant via lien partagé
          (autoplay audio bloqué par défaut sur navigateurs mobiles). Premier tap =
          user gesture qui débloque narration + musique simultanément. */}
      {showStartOverlay && (
        <div
          data-testid="demo-start-overlay"
          className="absolute inset-0 z-[10002] flex items-center justify-center px-6"
          style={{
            background:
              'radial-gradient(120% 80% at 50% 40%, rgba(28,17,71,0.92) 0%, rgba(10,8,32,0.97) 65%, rgba(5,3,20,1) 100%)',
            backdropFilter: 'blur(6px)',
            animation: 'demoFinalFadeIn 500ms ease-out forwards',
          }}
        >
          <div className="text-center max-w-sm mx-auto">
            <div
              className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                boxShadow: `0 16px 40px -12px ${T.gold}aa`,
              }}
            >
              <Volume2 className="w-10 h-10" style={{ color: T.ink }} strokeWidth={2.2} />
            </div>
            <p
              className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-3"
              style={{ color: T.goldLight }}
            >
              CoachDIA · Démo
            </p>
            <h2
              className="text-2xl sm:text-3xl mb-3"
              style={{ color: '#FFFFFF', fontFamily: 'Georgia, serif', fontWeight: 600, lineHeight: 1.2 }}
            >
              Appuyez pour lancer<br />la démo avec le son
            </h2>
            <p
              className="text-sm mb-7"
              style={{ color: 'rgba(255,255,255,0.65)' }}
            >
              {DEMO_SCENE_COUNT} scènes · {DEMO_TOTAL_DURATION_MIN} minutes · narration immersive
            </p>

            <button
              type="button"
              onClick={() => {
                // 🎚️ Init du graphe Web Audio sur ce user-gesture explicite
                //    (autorise GainNode sur iOS Safari/Chrome).
                ensureAudioGraph();
                setShowStartOverlay(false);
                setIsMuted(false);
                setIsPlaying(true);
                setAutoplayBlocked(false);
              }}
              data-testid="demo-start-with-sound-btn"
              className="w-full py-4 rounded-full text-base font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              style={{
                background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                color: T.ink,
                boxShadow: `0 14px 36px -12px ${T.gold}cc`,
              }}
            >
              <Volume2 className="w-5 h-5" />
              Lancer avec le son
            </button>

            <button
              type="button"
              onClick={() => {
                ensureAudioGraph();
                setShowStartOverlay(false);
                setIsMuted(true);
                setIsPlaying(true);
              }}
              data-testid="demo-start-silent-btn"
              className="mt-3 w-full py-3 rounded-full text-sm font-semibold transition-all hover:opacity-100"
              style={{
                background: 'transparent',
                color: 'rgba(255,255,255,0.7)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              Continuer sans son
            </button>

            <p
              className="text-[10px] mt-6"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              🔒 Vous arrivez via un lien partagé · L'audio doit être déclenché manuellement
            </p>
          </div>
        </div>
      )}

      {/* 🎯 Overlay final premium — DÉSACTIVÉ V3 (12/02/2026) : la scène 13 garde
           ses CTAs natifs "Commencer maintenant" / "Découvrir les parcours".
           L'overlay agressif (auto-redirect) a été retiré au profit d'une expérience
           premium non-intrusive. Le code est conservé pour future réactivation
           si besoin. Condition forcée `false` = jamais affiché. */}
      {false && showFinalOverlay && (
        <div
          data-testid="demo-final-overlay"
          className="absolute inset-0 z-[10001] flex items-center justify-center px-6"
          style={{
            background:
              'radial-gradient(120% 80% at 50% 30%, rgba(28,17,71,0.96) 0%, rgba(10,8,32,0.98) 70%, rgba(5,3,20,1) 100%)',
            animation: 'demoFinalFadeIn 700ms ease-out forwards',
          }}
        >
          <div className="max-w-xl w-full text-center">
            <p
              className="uppercase text-[10px] sm:text-[11px] tracking-[0.32em] mb-4 font-semibold"
              style={{ color: T.gold }}
            >
              CoachDIA · Votre parcours
            </p>
            <h2
              className="text-2xl sm:text-4xl leading-[1.15] mb-3"
              style={{
                color: '#FFF8E0',
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontWeight: 600,
                letterSpacing: '-0.015em',
                textShadow: '0 4px 24px rgba(212,175,55,0.25)',
              }}
            >
              Commencez maintenant votre parcours CoachDIA
            </h2>
            <p
              className="text-sm sm:text-base mb-7 max-w-md mx-auto"
              style={{ color: 'rgba(243,208,96,0.78)' }}
            >
              Transformez vos décisions en preuves. Passez d'une formation suivie à un leadership mesurable.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <button
                onClick={handlePrimaryCta}
                data-testid="demo-final-primary-cta"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-full text-sm font-bold transition hover:-translate-y-0.5"
                style={{
                  background:
                    'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                  color: '#1C1147',
                  boxShadow: '0 12px 32px -10px rgba(212,175,55,0.6)',
                  letterSpacing: '0.02em',
                  minWidth: '180px',
                }}
              >
                S'abonner →
              </button>
              <button
                onClick={handleSecondaryCta}
                data-testid="demo-final-secondary-cta"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-full text-sm font-semibold transition"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  color: '#F3D060',
                  border: '1px solid rgba(243,208,96,0.42)',
                  letterSpacing: '0.02em',
                  minWidth: '180px',
                }}
              >
                Découvrir les parcours
              </button>
            </div>
            <p
              className="text-[10px] sm:text-[11px] mt-6"
              style={{ color: 'rgba(243,208,96,0.5)' }}
            >
              Redirection automatique vers la souscription dans quelques secondes…
            </p>
          </div>
          <style>{`
            @keyframes demoFinalFadeIn {
              from { opacity: 0; transform: scale(1.02); }
              to   { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
